import { useEffect, useState } from 'react';
import { Copy, KeyRound, Trash2 } from 'lucide-react';
import { ApiError, apiFetch } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { Button } from '../components/Button.tsx';
import { Card } from '../components/Card.tsx';
import { useDocumentTitle } from '../components/useDocumentTitle.ts';

interface AdminApiKey {
  id: string;
  name: string;
  organizationId: string;
  organizationName: string | null;
  keyPrefix: string;
  scopes: string[];
  createdBy: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

interface ListResponse {
  keys: AdminApiKey[];
  allScopes: string[];
}

interface CreateResponse {
  key: AdminApiKey;
  plaintext: string;
}

interface OrgLite {
  id: string;
  name: string;
}

export function AdminApiKeysPage(): React.ReactElement {
  useDocumentTitle('API keys');
  const { agent: me } = useAuth();
  const [keys, setKeys] = useState<AdminApiKey[] | null>(null);
  const [allScopes, setAllScopes] = useState<string[]>([]);
  const [orgs, setOrgs] = useState<OrgLite[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showCreate, setShowCreate] = useState(false);
  const [name, setName] = useState('');
  const [organizationId, setOrganizationId] = useState('');
  const [selectedScopes, setSelectedScopes] = useState<string[]>(['time-data:read']);
  const [creating, setCreating] = useState(false);
  const [justCreated, setJustCreated] = useState<CreateResponse | null>(null);
  const [copied, setCopied] = useState(false);

  async function load() {
    try {
      const r = await apiFetch<ListResponse>('/api/admin/api-keys');
      setKeys(r.keys);
      setAllScopes(r.allScopes);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'Failed to load API keys.');
    }
  }

  async function loadOrgs() {
    try {
      const r = await apiFetch<{ organizations: OrgLite[] }>('/api/organizations');
      setOrgs(r.organizations);
    } catch {
      // non-fatal — admin can still see the keys list, just can't create
    }
  }

  useEffect(() => {
    void load();
    void loadOrgs();
  }, []);

  async function create(e: React.FormEvent) {
    e.preventDefault();
    if (!name.trim() || !organizationId || selectedScopes.length === 0) return;
    setCreating(true);
    setError(null);
    try {
      const r = await apiFetch<CreateResponse>('/api/admin/api-keys', {
        method: 'POST',
        json: { name: name.trim(), organizationId, scopes: selectedScopes },
      });
      setJustCreated(r);
      setName('');
      setOrganizationId('');
      setShowCreate(false);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to create key.');
    } finally {
      setCreating(false);
    }
  }

  async function revoke(id: string, label: string) {
    if (!window.confirm(`Revoke "${label}"? Any integration using this key will start failing immediately.`)) return;
    try {
      await apiFetch(`/api/admin/api-keys/${id}`, { method: 'DELETE' });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to revoke key.');
    }
  }

  function toggleScope(s: string) {
    setSelectedScopes((cur) => (cur.includes(s) ? cur.filter((x) => x !== s) : [...cur, s]));
  }

  async function copyPlaintext(plaintext: string) {
    try {
      await navigator.clipboard.writeText(plaintext);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard may be unavailable on http or sandboxed contexts; the
      // input is selectable as a fallback so we don't need to handle this.
    }
  }

  if (!me) return <p>Loading…</p>;
  if (me.role !== 'admin') {
    return (
      <Card>
        <p className="text-[var(--color-muted)]">Admins only.</p>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-3xl font-bold text-[var(--color-header)]">API keys</h1>
          <p className="mt-1 max-w-2xl text-[var(--color-muted)]">
            Bearer tokens for the public read-only API at <code>/api/v1</code>. Each key is bound
            to <strong>exactly one organization</strong> at creation — there is no way to read
            another org's data with any key. Issue one per integration so you can revoke
            individually if a key is exposed. Browse the spec at{' '}
            <a href="/api/v1/docs" target="_blank" rel="noreferrer" className="text-[var(--color-t3-blue)] hover:underline">
              /api/v1/docs
            </a>{' '}
            (Swagger UI).
          </p>
        </div>
        <Button onClick={() => setShowCreate((v) => !v)}>
          <KeyRound className="h-4 w-4" /> {showCreate ? 'Cancel' : 'New API key'}
        </Button>
      </div>

      {error && <p className="text-sm text-[var(--color-danger)]">{error}</p>}

      {justCreated && (
        <Card className="border-[var(--color-t3-blue)]/40 bg-[var(--color-t3-blue)]/5">
          <h2 className="text-base font-semibold text-[var(--color-header)]">
            Key created — copy it now
          </h2>
          <p className="mt-1 text-sm text-[var(--color-muted)]">
            Bound to <strong>{justCreated.key.organizationName ?? '(unknown org)'}</strong>. This is
            the only time the full key will be shown. After you leave this page you'll only see the
            prefix.
          </p>
          <div className="mt-3 flex items-stretch gap-2">
            <input
              readOnly
              value={justCreated.plaintext}
              onClick={(e) => (e.target as HTMLInputElement).select()}
              className="flex-1 rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 font-mono text-xs"
            />
            <Button onClick={() => void copyPlaintext(justCreated.plaintext)}>
              <Copy className="h-4 w-4" /> {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
          <div className="mt-3 text-xs text-[var(--color-muted)]">
            Use it like:{' '}
            <code className="rounded bg-white px-1 py-0.5">
              curl -H "Authorization: Bearer {justCreated.plaintext.slice(0, 12)}…" {window.location.origin}/api/v1/time-entries
            </code>
          </div>
          <div className="mt-3">
            <button
              type="button"
              onClick={() => setJustCreated(null)}
              className="text-xs text-[var(--color-muted)] hover:underline"
            >
              I've copied it — dismiss
            </button>
          </div>
        </Card>
      )}

      {showCreate && (
        <Card>
          <form onSubmit={create} className="space-y-4">
            <label className="block text-sm">
              <span className="block text-xs font-medium text-[var(--color-header)]">Name</span>
              <input
                type="text"
                required
                maxLength={100}
                placeholder="e.g. Acme Corp Power BI dashboard"
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="mt-1 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
              />
              <span className="mt-1 block text-xs text-[var(--color-muted)]">
                Just for your reference — what's this key for?
              </span>
            </label>
            <label className="block text-sm">
              <span className="block text-xs font-medium text-[var(--color-header)]">Organization</span>
              <select
                required
                value={organizationId}
                onChange={(e) => setOrganizationId(e.target.value)}
                className="mt-1 w-full rounded-[var(--radius-button)] border border-[var(--color-border)] bg-white px-3 py-2 text-sm"
              >
                <option value="">— Pick an organization —</option>
                {orgs.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
              <span className="mt-1 block text-xs text-[var(--color-muted)]">
                The key will only be able to read this org's data. Cannot be changed later.
              </span>
            </label>
            <div>
              <span className="block text-xs font-medium text-[var(--color-header)]">Scopes</span>
              <div className="mt-2 space-y-2">
                {allScopes.map((s) => (
                  <label key={s} className="flex items-center gap-2 text-sm">
                    <input
                      type="checkbox"
                      checked={selectedScopes.includes(s)}
                      onChange={() => toggleScope(s)}
                      className="h-4 w-4"
                    />
                    <code className="rounded bg-[var(--color-surface)] px-1.5 py-0.5 text-xs">{s}</code>
                  </label>
                ))}
              </div>
            </div>
            <div className="flex gap-2 border-t border-[var(--color-border)] pt-3">
              <Button
                type="submit"
                disabled={creating || !name.trim() || !organizationId || selectedScopes.length === 0}
              >
                {creating ? 'Creating…' : 'Create key'}
              </Button>
              <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>
                Cancel
              </Button>
            </div>
          </form>
        </Card>
      )}

      <Card className="overflow-hidden !p-0">
        <table className="w-full text-sm">
          <thead className="bg-[var(--color-surface)] text-left text-xs uppercase tracking-wide text-[var(--color-muted)]">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Organization</th>
              <th className="px-4 py-3">Prefix</th>
              <th className="px-4 py-3">Scopes</th>
              <th className="px-4 py-3">Created</th>
              <th className="px-4 py-3">Last used</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody>
            {keys === null ? (
              <tr>
                <td className="px-4 py-6 text-[var(--color-muted)]" colSpan={8}>
                  Loading…
                </td>
              </tr>
            ) : keys.length === 0 ? (
              <tr>
                <td className="px-4 py-6 text-[var(--color-muted)]" colSpan={8}>
                  No API keys yet.
                </td>
              </tr>
            ) : (
              keys.map((k) => (
                <tr key={k.id} className="border-t border-[var(--color-border)]">
                  <td className="px-4 py-3 font-medium text-[var(--color-header)]">{k.name}</td>
                  <td className="px-4 py-3">{k.organizationName ?? '—'}</td>
                  <td className="px-4 py-3 font-mono text-xs">{k.keyPrefix}…</td>
                  <td className="px-4 py-3">
                    {k.scopes.map((s) => (
                      <code key={s} className="mr-1 rounded bg-[var(--color-surface)] px-1.5 py-0.5 text-xs">
                        {s}
                      </code>
                    ))}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-muted)]">
                    {new Date(k.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-4 py-3 text-[var(--color-muted)]">
                    {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : '—'}
                  </td>
                  <td className="px-4 py-3">
                    {k.revokedAt ? (
                      <span className="rounded-full bg-[var(--color-surface)] px-2 py-0.5 text-xs text-[var(--color-muted)]">
                        revoked
                      </span>
                    ) : (
                      <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-xs text-emerald-700">
                        active
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right">
                    {!k.revokedAt && (
                      <button
                        type="button"
                        onClick={() => void revoke(k.id, k.name)}
                        className="inline-flex items-center gap-1 text-xs text-[var(--color-danger)] hover:underline"
                      >
                        <Trash2 className="h-3.5 w-3.5" /> Revoke
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
